import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLang, usePageTitle } from "@/i18n/LanguageContext";
import { submitInquiry, validateInquiry, INQUIRY_MODE, CONTACT_EMAIL } from "@/services/inquiry";
import { pages } from "../content/pages";
import { useContent, Rich, plainText } from "../ui/helpers";
import { PageShell } from "../ui/blocks";

/*
  CONTACT — text: src/site/content/pages.js (contact)
            sending: src/services/inquiry.js (mailto or API, set in frontend/.env)
  States: idle → sending → ok | error (with retry). Field errors are shown
  next to each field and announced to screen readers.
*/
const EMPTY = { name: "", company: "", email: "", message: "", website: "" }; // `website` = hidden spam trap
const FIELDS = ["name", "company", "email", "message"];

export default function ContactPage() {
  usePageTitle("contact");
  const { lang } = useLang();
  const c = useContent(pages).contact;
  const common = useContent(pages).common;
  const f = c.form;
  const [topic, setTopic] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ status: "idle", code: "" }); // idle | sending | ok | error
  const [copied, setCopied] = useState(false);
  const formRef = useRef(null);
  const statusRef = useRef(null);

  const set = (k) => (e) => {
    setData((d) => ({ ...d, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => { const n = { ...x }; delete n[k]; return n; });
  };

  const send = async () => {
    const payload = { ...data, topic: plainText(c.topics[topic].title), lang };
    const v = validateInquiry(payload);
    if (!v.ok) {
      setErrors(v.errors);
      setState({ status: "idle", code: "" });
      const first = FIELDS.find((k) => v.errors[k]);
      formRef.current?.querySelector(`#f-${first}`)?.focus();
      return;
    }
    setErrors({});
    setState({ status: "sending", code: "" });
    try {
      await submitInquiry(payload);
      setState({ status: "ok", code: "" });
      if (INQUIRY_MODE === "api") setData(EMPTY);
    } catch (e) {
      setState({ status: "error", code: e?.code || "server" });
    }
    setTimeout(() => statusRef.current?.focus(), 0);
  };

  const onSubmit = (e) => { e.preventDefault(); send(); };

  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(CONTACT_EMAIL).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); }).catch(() => {});
  };

  const errText = (k) => (errors[k] ? f.errors[k === "email" ? "email" : k] : "");
  const field = (k, label, input) => (
    <div className={`k-field${k === "message" ? " full" : ""}${errors[k] ? " has-err" : ""}`}>
      <label htmlFor={`f-${k}`}>{label}</label>
      {input}
      {errors[k] && <p className="field-err" id={`f-${k}-err`}>{errText(k)}</p>}
    </div>
  );
  const aria = (k) => ({ "aria-invalid": errors[k] ? true : undefined, "aria-describedby": errors[k] ? `f-${k}-err` : undefined, required: true });
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <PageShell testId="contact-page">
      <section className="contact">
        <div className="k-wrap contact-top rv">
          <nav className="crumbs" aria-label={common.breadcrumb}><Link to="/">{common.home}</Link><span aria-hidden="true">/</span><span aria-current="page">{c.crumb}</span></nav>
          <p className="k-label">{c.eyebrow}</p>
          <h1><Rich text={c.title} /></h1>
        </div>
        <div className="k-wrap contact-grid">
          <div className="contact-intro rv">
            <p className="lead">{c.lead}</p>

            <fieldset className="topics">
              <legend className="sr">{c.topicsLabel}</legend>
              {c.topics.map((t, i) => (
                <label key={t.title} className={`topic${i === topic ? " on" : ""}`}>
                  <input className="sr" type="radio" name="topic" value={i} checked={i === topic} onChange={() => setTopic(i)} />
                  <i aria-hidden="true">{t.icon}</i><span><strong>{t.title}</strong><span>{t.text}</span></span><span className="radio" aria-hidden="true" />
                </label>
              ))}
            </fieldset>

            <dl className="cinfo">
              <div>
                <dt>{c.emailLabel}</dt>
                <dd className="cinfo-mail">
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                  <button type="button" className="tbtn" onClick={copy} aria-live="polite">{copied ? common.copied : common.copy}</button>
                </dd>
              </div>
              <div><dt>{c.replyLabel}</dt><dd>{c.reply}</dd></div>
            </dl>

          </div>

          <form className="k-form rv" ref={formRef} onSubmit={onSubmit} noValidate data-testid="contact-form" aria-busy={state.status === "sending"}>
            <h2>{f.title}</h2>
            <p className="form-req">{f.required}</p>

            {hasErrors && <p className="form-msg err" role="alert">{f.errors.summary}</p>}

            <p className="form-topic">{c.topicsLabel}: <strong>{c.topics[topic].title}</strong></p>

            <div className="fgrid">
              {field("name", f.name, <input id="f-name" value={data.name} onChange={set("name")} placeholder={f.namePh} autoComplete="name" maxLength={120} {...aria("name")} />)}
              {field("company", f.company, <input id="f-company" value={data.company} onChange={set("company")} placeholder={f.companyPh} autoComplete="organization" maxLength={160} {...aria("company")} />)}
              {field("email", f.email, <input id="f-email" type="email" inputMode="email" value={data.email} onChange={set("email")} placeholder={f.emailPh} autoComplete="email" {...aria("email")} />)}
              {field("message", f.message, <textarea id="f-message" rows={6} value={data.message} onChange={set("message")} placeholder={f.messagePh} maxLength={5000} {...aria("message")} />)}
              {/* Spam trap: hidden from people, bots tend to fill it in. The backend ignores entries that have it. */}
              <div className="trap" aria-hidden="true"><label htmlFor="f-website">Website</label><input id="f-website" type="text" name="website" value={data.website} onChange={set("website")} tabIndex={-1} autoComplete="off" /></div>
            </div>

            <div className="form-foot">
              <p className="form-note">{INQUIRY_MODE === "api" ? f.noteApi : f.noteMailto}</p>
              <button className="k-btn k-btn-mint" type="submit" disabled={state.status === "sending"}>
                {state.status === "sending" ? f.sending : INQUIRY_MODE === "api" ? f.submitApi : f.submitMailto}
              </button>
            </div>

            <div ref={statusRef} tabIndex={-1} className="form-status" role="status" aria-live="polite">
              {state.status === "ok" && <p className="form-msg ok">{INQUIRY_MODE === "api" ? f.okApi : f.okMailto}</p>}
              {state.status === "error" && (
                <div className="form-msg err">
                  <p>{f.errors[state.code] || f.errors.server}</p>
                  <p className="form-msg-actions">
                    {state.code !== "rate" && state.code !== "invalid" && <button type="button" className="tbtn" onClick={send}>{f.retry}</button>}
                    <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
        <div className="k-wrap">
          <h2 className="next-title rv">{c.nextLabel}</h2>
          <ol className="next3 rv">{c.next.map(([t, s], i) => <li key={t}><b>{String(i + 1).padStart(2, "0")}</b><strong>{t}</strong><span>{s}</span></li>)}</ol>
        </div>
      </section>
    </PageShell>
  );
}
